import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  isPushSupported,
  isNativePushSupported,
  getPermissionAsync,
  subscribeToPush,
  PushPermissionError,
} from "../../pwa/pushClient";
import { isFinancialManager } from "../../utils/roles";

/**
 * Silently re-arm this device's push subscription after login.
 *
 * Logging out tears the subscription down on purpose — both the browser
 * subscription and the server-side record (see useLogout → unsubscribeFromPush)
 * — so a signed-out device stops receiving the previous user's leads. But two
 * things survive that teardown: the browser's notification *permission*
 * (origin-scoped, not session-scoped) and the user's `pushEnabled` preference.
 * So after the next login the only missing piece is the subscription itself.
 * Re-create it in the background instead of making the user walk through the
 * enable flow again.
 *
 * Guards, in order:
 *   - an authenticated, non-impersonated session is required. Binding the
 *     admin's browser endpoint to an impersonated user would hijack the admin's
 *     own notifications, so we skip while "Log in as client" is active.
 *   - on the web, push must be supported and permission already "granted". We
 *     NEVER prompt here — a first-time user still opts in explicitly once via
 *     Settings; this only restores an opt-in that already happened.
 *   - on the web, `pushEnabled !== false`, so we don't resurrect a subscription
 *     the user deliberately turned OFF via the Settings toggle. Logout leaves
 *     the flag true; the toggle's off-path sets it false. The login response
 *     carries the fresh value, which is what makes the two cases
 *     distinguishable.
 *
 * Inside the iOS store app the phone itself is the opt-in (Mostafa,
 * 2026-10-23): the first open after login asks iOS — its own dialog, no sheet
 * and no switch in the app — and registers the phone; the server greets it
 * with one welcome push. Later logins re-register silently. A refusal is
 * left alone (NotificationGate reminds every three days where in iPhone
 * Settings to allow it), and `pushEnabled` is not consulted: the app cannot
 * be "off", registering turns the flag back on server-side. Financial
 * Managers get no lead alerts, so their phones are never asked.
 *
 * Runs at most once per logged-in user; the ref resets on logout so logging
 * back in re-arms. Fire-and-forget — login must never block on, or fail
 * because of, push. A failed attempt (missing SW, offline network, dead sub,
 * transient server error) is logged and retried a few times with backoff
 * instead of failing silently for the rest of the session — otherwise a race
 * at SW-rollout time can permanently strand a device off until the user
 * happens to revisit Settings. A refusal of the permission dialog is final
 * for the session: no retry.
 */
const RETRY_DELAYS_MS = [3000, 10000, 30000];
// Let the first screen paint before iOS puts its dialog over it.
export const FIRST_ASK_DELAY_MS = 1500;

export const usePushAutoResubscribe = () => {
  const token = useSelector((state) => state.auth?.token);
  const user = useSelector((state) => state.auth?.user);
  const userId = user?._id;
  const pushEnabled = user?.pushEnabled;
  const impersonator = useSelector((state) => state.auth?.impersonator);
  // Tracks the userId we've successfully re-armed for. Reset to null on
  // logout so a subsequent login (even as the same user) re-arms after the
  // teardown.
  const restoredForRef = useRef(null);

  useEffect(() => {
    if (!token || !userId) {
      restoredForRef.current = null;
      return;
    }
    if (impersonator) return;
    const native = isNativePushSupported();
    if (!native && pushEnabled === false) return;
    if (native && isFinancialManager(user)) return;
    if (restoredForRef.current === userId) return;
    if (!isPushSupported()) return;

    // Mark synchronously (not in a resolve handler) so a redundant effect run
    // in the same session — e.g. a token refresh for this same user — doesn't
    // kick off a second, parallel attempt. Retries on failure are handled
    // internally below via the timer chain, independent of whether the
    // effect re-runs.
    restoredForRef.current = userId;

    let cancelled = false;
    const timers = [];

    const attempt = (attemptIndex) => {
      subscribeToPush(token).catch((err) => {
        if (cancelled) return;
        if (err instanceof PushPermissionError) {
          // The person answered the dialog with no (or closed it): their
          // call, not a fault to retry. The mark is lifted so a later login
          // asks again if iOS still allows asking.
          restoredForRef.current = null;
          return;
        }
        const delay = RETRY_DELAYS_MS[attemptIndex];
        console.warn(
          `[push] auto-resubscribe attempt ${attemptIndex + 1} failed` +
            (delay ? `, retrying in ${delay}ms` : ", giving up for this session") +
            ":",
          err
        );
        if (delay !== undefined) {
          timers.push(setTimeout(() => attempt(attemptIndex + 1), delay));
        }
        /* Exhausted retries: still best-effort — the user can still re-enable
           via Settings. */
      });
    };

    // Permission is read asynchronously (inside the iOS app it comes from the
    // plugin). On the web anything but "granted" means this device never
    // opted in — or was blocked — so there is nothing to restore; the mark is
    // lifted so a later run of the effect (token refresh) can look again. In
    // the store app "default" (never asked) is the cue to ask now.
    getPermissionAsync()
      .then((permission) => {
        if (cancelled) return;
        if (permission === "granted") {
          attempt(0);
          return;
        }
        if (native && permission === "default") {
          timers.push(setTimeout(() => attempt(0), FIRST_ASK_DELAY_MS));
          return;
        }
        restoredForRef.current = null;
      })
      .catch(() => {
        if (!cancelled) restoredForRef.current = null;
      });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [token, user, userId, pushEnabled, impersonator]);
};
